import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { User } from "@shared/core/types";
import { TENANT_ROLE_LABELS, type Tenant, type TenantMember, type TenantUserRole } from "@shared/modules/tenants";
import { api } from "../../../core/lib/api";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Modal } from "../../../core/components/ui/modal";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { useTenantStore } from "../stores/tenant";

export function TenantSettings() {
  const { tenantId = "" } = useParams();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const { links, load } = useTenantStore();
  const isAdmin = me?.role === "admin";
  const canEdit = isAdmin || links.some((l) => l.tenantId === tenantId && l.role === "tenant_owner");

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newRole, setNewRole] = useState<TenantUserRole>("tenant_staff");

  const loadMembers = () => api<TenantMember[]>(`/api/tenants/${tenantId}/members`).then(setMembers).catch(() => setMembers([]));

  useEffect(() => {
    api<Tenant>(`/api/tenants/${tenantId}`)
      .then((t) => {
        setTenant(t);
        setName(t.name);
        setTimezone(t.timezone);
      })
      .catch(() => push("danger", "Établissement introuvable"));
    void loadMembers();
    if (isAdmin) api<User[]>("/api/users").then(setUsers).catch(() => setUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, isAdmin]);

  const save = async () => {
    setBusy(true);
    try {
      const t = await api<Tenant>(`/api/tenants/${tenantId}`, { method: "PATCH", body: JSON.stringify({ name, timezone }) });
      setTenant(t);
      push("success", "Établissement enregistré");
      await load();
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const addMember = async () => {
    if (!newUserId) return;
    setBusy(true);
    try {
      await api(`/api/tenants/${tenantId}/members`, { method: "PUT", body: JSON.stringify({ userId: newUserId, role: newRole }) });
      setNewUserId("");
      await loadMembers();
      push("success", "Membre rattaché");
    } catch {
      push("danger", "Rattachement impossible");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (userId: string) => {
    try {
      await api(`/api/tenants/${tenantId}/members/${userId}`, { method: "DELETE" });
      await loadMembers();
    } catch {
      push("danger", "Retrait impossible");
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api(`/api/tenants/${tenantId}`, { method: "DELETE" });
      push("success", "Établissement supprimé");
      await load();
      navigate("/etablissements");
    } catch {
      push("danger", "Suppression impossible");
      setBusy(false);
    }
  };

  const candidates = users.filter((u) => !members.some((m) => m.userId === u.id));

  return (
    <div className="page">
      <PageHeader
        title={tenant?.name ?? "Établissement"}
        subtitle="Identité, fuseau horaire et membres."
        actions={
          isAdmin ? (
            <Button variant="ghost-danger" onClick={() => setConfirmDelete(true)}>
              Supprimer
            </Button>
          ) : undefined
        }
      />

      <div className="settings-grid">
        <Card
          title="Identité"
          footer={
            canEdit ? (
              <Button variant="primary" onClick={save} disabled={busy || !name.trim()}>
                Enregistrer
              </Button>
            ) : undefined
          }
        >
          <div className="form-grid">
            <Field label="Nom">
              <Input value={name} onChange={(e) => setName(e.target.value)} readOnly={!canEdit} />
            </Field>
            <Field label="Fuseau horaire">
              <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} readOnly={!canEdit} />
            </Field>
          </div>
        </Card>

        <Card title="Membres" subtitle="Comptes qui voient cet établissement.">
          {members.length === 0 ? <p className="muted">Aucun membre rattaché.</p> : null}
          <ul className="member-list">
            {members.map((m) => (
              <li key={m.userId} className="member-list__item">
                <span className="member-list__who">
                  <span className="member-list__name">{m.name}</span>
                  <span className="member-list__email">{m.email}</span>
                </span>
                <Badge variant={m.role === "tenant_owner" ? "primary" : "neutral"}>{TENANT_ROLE_LABELS[m.role]}</Badge>
                {isAdmin ? (
                  <Button variant="ghost-danger" size="sm" onClick={() => removeMember(m.userId)}>
                    Retirer
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {isAdmin ? (
            <div className="member-list__add">
              <Select value={newUserId} onChange={(e) => setNewUserId(e.target.value)} aria-label="Compte à rattacher">
                <option value="">Choisir un compte…</option>
                {candidates.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} — {u.email}
                  </option>
                ))}
              </Select>
              <Select value={newRole} onChange={(e) => setNewRole(e.target.value as TenantUserRole)} aria-label="Rôle">
                <option value="tenant_staff">{TENANT_ROLE_LABELS.tenant_staff}</option>
                <option value="tenant_owner">{TENANT_ROLE_LABELS.tenant_owner}</option>
              </Select>
              <Button variant="ghost" onClick={addMember} disabled={busy || !newUserId}>
                Rattacher
              </Button>
            </div>
          ) : null}
        </Card>
      </div>

      <Modal
        open={confirmDelete}
        title="Supprimer cet établissement ?"
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={remove} disabled={busy}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>Les rattachements de membres seront retirés. Cette action est définitive.</p>
      </Modal>
    </div>
  );
}
